// useTopicsDraft — el estado del paso de temas de Novah.
//
// Qué hace: la lista de temas guardados, la caja en curso, la memoria de la
// última tanda y toda la aritmética derivada (cuánto pidieron los temas, cuántos
// posts salen al final, si se pasaron del presupuesto).
//
// Por qué vive acá y no en NovahGenerateChat: ese archivo ya pasa las 500 líneas
// del tope del repo, así que lo que se agrega se extrae.
//
// Qué exporta: useTopicsDraft, TopicDraft, MAX_TOPICS.
// Quién lo usa: NovahGenerateChat (y por props, TopicsStep y ConfirmSummary).
import { useCallback, useMemo, useRef, useState } from 'react';
import { TopicRequest } from '@agency-smm/shared';

export interface TopicDraft {
  id: number;
  topic: string;
  count: number;
}

export const MAX_TOPICS = 10;
export const MAX_PER_TOPIC = 10;
export const TOPIC_COUNT_OPTIONS = [1, 2, 3, 4, 5, 10];

const lastUsedKey = (workspaceId: string) => `novah:lastTopics:${workspaceId}`;

/**
 * Lee la última tanda del navegador. Todo lo que viene de localStorage se trata
 * como texto ajeno: puede estar editado a mano o ser de una versión vieja, y de
 * acá salen números que después alimentan el conteo de posts.
 */
function readLastUsed(workspaceId: string): TopicDraft[] {
  try {
    const raw = localStorage.getItem(lastUsedKey(workspaceId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((t): t is { topic: string; count: number } =>
        !!t && typeof t === 'object'
        && typeof (t as { topic?: unknown }).topic === 'string'
        && Number.isFinite((t as { count?: unknown }).count as number))
      .slice(0, MAX_TOPICS)
      .map((t, i) => ({
        id: -(i + 1),
        topic: t.topic.slice(0, 200),
        count: Math.min(MAX_PER_TOPIC, Math.max(1, Math.floor(t.count))),
      }))
      .filter(t => t.topic.trim().length > 0);
  } catch {
    // Storage bloqueado / modo incógnito: la pantalla anda igual, sin el atajo.
    return [];
  }
}

export function useTopicsDraft(workspaceId: string, budgetPerPlatform: number, platformCount: number) {
  const [list, setList] = useState<TopicDraft[]>([]);
  const [draftText, setDraftText] = useState('');
  const [draftCount, setDraftCount] = useState(2);
  // Se lee una sola vez al montar: si se releyera en cada render, el botón
  // "usar los de la última vez" reaparecería después de guardar la tanda nueva.
  const [lastUsed] = useState<TopicDraft[]>(() => readLastUsed(workspaceId));
  const nextId = useRef(1);

  const addTopic = useCallback(() => {
    const topic = draftText.trim();
    if (!topic || list.length >= MAX_TOPICS) return;
    setList(prev => [...prev, { id: nextId.current++, topic, count: draftCount }]);
    // La caja se vacía pero el cupo NO se resetea: "2 posts por tema en 5 temas"
    // se elige una vez, no cinco.
    setDraftText('');
  }, [draftText, draftCount, list.length]);

  const updateTopic = useCallback((id: number, patch: Partial<Omit<TopicDraft, 'id'>>) => {
    setList(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const removeTopic = useCallback((id: number) => {
    setList(prev => prev.filter(t => t.id !== id));
  }, []);

  const applyLastUsed = useCallback(() => {
    setList(lastUsed.map(t => ({ ...t, id: nextId.current++ })));
  }, [lastUsed]);

  const clear = useCallback(() => {
    setList([]);
    setDraftText('');
  }, []);

  /**
   * Guarda en la caja lo que quedó tipeado y devuelve la lista final. Sin esto,
   * escribir el último tema y apretar Continue lo pierde en silencio — el bug
   * clásico de todo input de tags.
   */
  const commitDraft = useCallback((): TopicDraft[] => {
    const topic = draftText.trim();
    if (!topic || list.length >= MAX_TOPICS) return list;
    const committed = [...list, { id: nextId.current++, topic, count: draftCount }];
    setList(committed);
    setDraftText('');
    return committed;
  }, [draftText, draftCount, list]);

  const derived = useMemo(() => {
    const clean = list.filter(t => t.topic.trim().length > 0);
    const demand = clean.reduce((sum, t) => sum + t.count, 0);
    // Los temas mandan: el presupuesto del paso de cantidad es una sugerencia.
    const effectivePerPlatform = Math.max(budgetPerPlatform, demand);
    return {
      demand,
      effectivePerPlatform,
      totalPosts: platformCount * effectivePerPlatform,
      status: (demand === 0 ? 'empty' : demand < budgetPerPlatform ? 'under' : demand === budgetPerPlatform ? 'exact' : 'over') as
        'empty' | 'under' | 'exact' | 'over',
      toRequest: (): TopicRequest[] => clean.map(t => ({ topic: t.topic.trim(), count: t.count })),
    };
  }, [list, budgetPerPlatform, platformCount]);

  const persistLastUsed = useCallback((finalList: TopicDraft[]) => {
    try {
      const clean = finalList.filter(t => t.topic.trim().length > 0);
      if (clean.length === 0) return;
      localStorage.setItem(lastUsedKey(workspaceId), JSON.stringify(
        clean.map(t => ({ topic: t.topic.trim(), count: t.count })),
      ));
    } catch { /* storage bloqueado: no es motivo para romper la generación */ }
  }, [workspaceId]);

  return {
    list, draftText, draftCount, lastUsed,
    setDraftText, setDraftCount,
    addTopic, updateTopic, removeTopic, applyLastUsed, clear, commitDraft, persistLastUsed,
    ...derived,
  };
}

export type TopicsDraft = ReturnType<typeof useTopicsDraft>;
